import {
  isOngoing,
  isCompleted,
  isUpcoming,
  getInfoString
} from '@models/Course';
import { getQuizState, getSpecialInfoString } from '@models/Item';
import { programmeTypes, languageTypes, sessionStates } from '@constants/index';
import { compareDates, addTime } from '@utils/dates';
import { getUserModel } from '../utils';

export const getSlides = (pdf) => ({
  withAnnotations: pdf.withAnnotation,
  withoutAnnotations: pdf.noAnnotation
});

export const getLatestSession = (session) => ({
  correctCount: session.correctCount,
  incorrectCount: session.incorrectCount,
  finishedAt: session.finishedAt ? new Date(session.finishedAt) : null,
  isLive: session.isLive,
  score: session.score,
  skippedCount: session.skippedCount,
  startTime: session.startTime,
  uid: session.uid
});

const getDefaultThumbnail = (course) => {
  if (
    course.forPlus !== false &&
    course.programmeType === programmeTypes.TESTSERIES
  ) {
    return 'https://static.uacdn.net/thumbnail/course/testseries-placeholder.png';
  }
  return null;
};

export const lessonModel = (itemdata) => {
  const data = {
    week: itemdata.week,
    type: itemdata.type ?? 'post',
    rank: itemdata.rank,
    contentRank: itemdata.contentTypeItemRank,
    courseUID: itemdata.courseUID,
    courseSlug: itemdata.courseSlug,
    batchGroup: itemdata?.batchGroup ?? null,
    topicGroups: itemdata.topicGroups ?? []
  };

  if (data.type === 'live_class' || data.type === 'LiveClass') {
    data.type = 'post';
  }
  if (data.type === 'Test' || data.type === 'Quiz') {
    data.type = 'quiz';
  }
  const item = itemdata.properties ? itemdata.properties : itemdata;
  data.uid = item.uid;
  data.displayLanguage = item.languageDisplay;
  data.name = item.name || item.title;
  data.description = item.description || null;
  data.permalink = item.permalink;
  data.relativeLink = item.relativeLink;
  data.createdAt = item.createdAt;
  data.slug = item.slug;
  data.language = item.language;
  data.thumbnail = item.thumbnailV1 || item.thumbnail || item.video?.thumbnail;
  data.thumbnailV1 = item.thumbnailV1 || data.thumbnail;
  data.isSpecial = item.isSpecial || item.programme?.isSpecial;
  data.classType = item.classType;
  data.collectionName = item.collectionName;
  data.collection = item.collection;
  data.liveAt = item.liveAt ? new Date(item.liveAt) : new Date(item.startTime);
  data.liveAtString = item.liveAt || item.startTime;
  data.liveAtTime = new Date(item.liveAt || item.startTime).getTime();
  data.startedAt = item.startedAt ? new Date(item.startedAt) : null;
  data.author = itemdata.author ? getUserModel(itemdata.author) : null;
  data.startsAt = item.startTime || null;
  data.goalUid = item.goalUid || null;
  data.goalName = item.goalName || null;
  data.isOffline = itemdata.isOffline ?? false;
  data.isLiveStreamingEnabled = itemdata.isLiveStreamingEnabled ?? true;
  data.programmeUid =
    item.programmeUid || item.programme?.uid || item.collection?.uid;

  if (item.programme || item.collection) {
    data.programme = item.programme || item.collection;
  }

  if (!data.contentRank)
    data.contentRank = item.contentTypeItemRank || item.rank;

  data.hasWatched = item.hasWatched || item.watched || item.hasAttempted;
  data.liveClassUrl = item.liveClassUrl;
  data.videoUrl = item.videoUrl || item.video?.url;
  data.duration = item.duration;
  data.lessonDuration =
    item.videoDuration || item.video?.duration || item.duration;
  data.endsAt = addTime(data.liveAt, data.lessonDuration, 's');
  data.finishedAt = item.finishedAt ? new Date(item.finishedAt) : data.endsAt;
  data.embedUrl = item.video?.embedUrl;
  data.cdnUrl = item.video?.cdnUrl;
  data.videoType = item.video?.type;
  data.youtubeId = item.video?.youtubeId;
  data.key = item.distributionKey ? item.distributionKey : item.key || null;
  data.isPdfAvailable = item.video?.isPdfAvailable;
  data.pdfUrl = item.video?.pdfUrl;
  data.secondsBeforeLive = item.secondsBeforeLive;
  data.state = data.type === 'post' ? item.state : getQuizState(data);
  data.isLive =
    item.isLive || data.type === 'post'
      ? item.state === sessionStates.LIVE
      : data.secondsBeforeLive === 0 &&
        compareDates(new Date(), data.startsAt) === 0;
  if (data.type === 'quiz')
    data.isLive = getQuizState(data) === sessionStates.LIVE;
  data.watchTime = item.watchTime;
  data.slides = item.slidesPdf ? getSlides(item.slidesPdf) : null;
  data.latestSession = item.latestSession
    ? getLatestSession(item.latestSession)
    : null;
  data.totalQuestions = item.numQuestions;
  data.totalScore = item.totalScore;
  data.upvotes = item.recommendCount;
  data.goal = { topology: item.goal };
  data.queryID = item.queryID;
  data.requestID = item.requestID;
  data.index = item.index;
  data.specialInfoString =
    data.type === 'post' || data.classType === 1
      ? getSpecialInfoString(data)
      : '';

  data.isBookmarked = item.isBookmarked || item.bookmarked || false;
  data.ogImage = item.ogImage || null;
  data.programmeNM = item.collection2;
  data.liveClassDetails = item.value?.liveClass || item?.liveClass2;
  data.liveLearnersCount = item.liveLearnersCount;
  data.totalWatchTime = item.totalWatchTime;
  data.likePercentage = item.likePercentage;
  data.topology = item.topology || null;
  data.tags = item.tags ?? [];
  data.isSaved = item.isSaved;
  data.contentState = item.contentState || null;
  data.course = item.course;
  data.attendance = item.attendance;
  data.notesCount = item.notesCount;
  data.practiceDpp = itemdata?.practiceDpp || itemdata?.value?.practiceDpp;
  return data;
};

export const getTrackModel = (course) => {
  if (!course) {
    return null;
  }

  return {
    ...course,
    liveAtString: course.liveAt || course.startTime || null,
    liveAtTime: new Date(course.liveAt || course.startTime).getTime() || 0
  };
};

export const getCourseModel = (course) => {
  const defaultTopicGroups = course.topicGroup ? [course.topicGroup] : [];
  const data = {
    ...course,
    thumbnail:
      course.thumbnail ||
      course.coverPhoto ||
      getDefaultThumbnail(course) ||
      null,
    averageRating: course.avgRating,
    forPlus: course.forPlus || course.forSubscription || false,
    previewVideo: course.introVideo || null,
    language: course?.languageDisplay || course?.multipleLanguageDisplay?.[0],
    liveClassCount: course.sessionsCount || null,
    isBookmarked: course.bookmarked || null,
    courseType: course.programmeType || null,
    state: course.state ?? course?.status ?? null,
    liveClassesRemaining: course.sessionsRemaining || null,
    itemCount: course.allItemCount || course.itemCount || 0,
    isUpcoming: isUpcoming(course.startsAt),
    isOngoing: isOngoing(course.startsAt, course.endsAt),
    isCompleted: course.isCompleted || isCompleted(course.endsAt),
    quizCount: course.allItemCount - course.sessionsCount,
    channelDetails: course.channel || null,
    nextTrack: getTrackModel(
      course.nextTrack ||
        course.nextSession ||
        course.nextClass ||
        course.nextItem
    ),
    previousTrack: course.previousSession || null,
    author: getUserModel(course.author),
    authors: course.authors
      ? course.authors.map((author) =>
          getUserModel({
            ...author.authorDetails,
            isTaAuthor: author.isTaAuthor
          })
        )
      : [getUserModel(course.author)],
    topicGroup: course.topicGroup || null,
    topicGroups: course.topicGroups || defaultTopicGroups,
    topic: course?.topicGroups?.[0]?.name || null,
    conceptTopology: course?.conceptTopology?.name || null,
    newThumbnail: course.coverPhotoV1 || null,
    coverPhoto:
      course.coverPhotoV1 ||
      course.coverPhoto ||
      course.thumbnail ||
      getDefaultThumbnail(course),
    goal: course.goal || null,
    topologies: course.topologies || null,
    goalUid: course.goalUid || course.goal?.uid || null,
    liveClasses: course.liveClasses
      ? course.liveClasses.map((itemData) => ({ item: itemData }))
      : null,
    conceptLessonCount: course.lessonCount || null,
    batchGroup: course.batchGroup || null
  };

  if (course.progress) {
    data.progress.nextLessonRelativeLink =
      course.progress.nextLessonRelativeLink;
    data.progress.isCompleted = parseInt(course.progress.percentage, 2) === 100;
  }

  data.infoString = getInfoString(data);
  data.processed = true;

  return data;
};

export const processCourseData = (data) => {
  const { languages: newLanguages = [] } = data;

  const voiceLanguages = newLanguages.filter(
    (lang) => lang.languageType === languageTypes.SPOKEN
  );

  const contentSlideLanguages = newLanguages.filter(
    (lang) => lang.languageType === languageTypes.CONTENT_SLIDE_LANGUAGE
  );

  return getCourseModel({
    ...data,
    ...data.programme,
    voiceLanguages,
    contentSlideLanguages
  });
};

export const processSimilarPlusCoursesData = (
  courseUID,
  similarCoursesData
) => {
  const updatedData = {
    ...similarCoursesData,
    extraBlockInfo: {
      ...(similarCoursesData.extraBlockInfo || {}),
      entityType: 'Course'
    }
  };

  updatedData.data = similarCoursesData.data.map((course) =>
    processCourseData(course)
  );

  updatedData.key = courseUID;

  return updatedData;
};

export const processRelevantFreeCoursesData = (data) => {
  return data.map((course) => processCourseData(course));
};

export const processLessonList = (data) => {
  const updatedData = {
    processed: true,
    ...data
  };

  updatedData.results = data?.results?.map((resultItem) => {
    const resultData = {
      ...resultItem.value.liveClass,
      ...resultItem.value,
      ...resultItem
    };

    resultData.liveClass2 = { ...resultItem.value.liveClass };

    delete resultData.liveClass;
    delete resultData.collection;
    delete resultData.value;

    return lessonModel(resultData);
  });

  return updatedData;
};

export const processNextTrack = (data) => {
  const { activeQuizSession } = data;

  if (activeQuizSession) {
    delete activeQuizSession.collection;
    delete activeQuizSession.programme;
  }

  const newData = {
    processed: true,
    nextTrack: data.nextTrack
      ? lessonModel({ ...data.nextTrack, type: 'post' })
      : null,
    activeQuizSession: activeQuizSession
      ? lessonModel({ ...activeQuizSession, type: 'quiz' })
      : null
  };
  return newData;
};
